import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { authGuard, AuthService, guestGuard, superAdminOnlyGuard } from '@core';
import { BlockComponent, CheckSessionComponent, LoginComponent } from '@features/auth';

/** Local Imports */
import {
    AdvertisersComponent,
    CreateAdvertiserProfileComponent,
    CreatePlaylistComponent,
    CreateUserComponent,
    DashboardComponent,
    DealersComponent,
    HostsComponent,
    InstallationsComponent,
    LicensesComponent,
    LocatorComponent,
    MediaLibraryComponent,
    PlaylistsComponent,
    ProfileSettingsComponent,
    ScreensComponent,
    SingleAdvertiserComponent,
    SingleDealerComponent,
    SingleHostComponent,
    SingleLicenseComponent,
    SingleMediaLibraryComponent,
    SinglePlaylistComponent,
    SingleScreenComponent,
    SingleUserComponent,
    TemplatesComponent,
    UsersComponent,
} from '@features';
import { CallbackComponent } from '@features/auth/views/callback/callback.component';
import { SampleComponent } from '@features/sample/sample.component';
import { MainLayoutComponent } from '@layouts/main-layout/main-layout.component';

/**
 * Restriction guard to prevent Dealers and Dealer Admins from accessing users management.
 */
const dealerRestrictionGuard = () => {
    const role = inject(AuthService).getUserTraits()?.data?.roleName;
    return role === 'Dealer' || role === 'Dealer Admin'
        ? inject(Router).createUrlTree(['/dashboard'])
        : true;
};

export const routes: Routes = [
    {
        path: 'auth',
        component: CallbackComponent,
    },
    {
        path: 'login',
        component: LoginComponent,
        canMatch: [guestGuard],
    },
    {
        path: 'block',
        component: BlockComponent,
    },
    {
        path: 'users/create',
        component: CreateUserComponent,
        canMatch: [authGuard, dealerRestrictionGuard],
    },
    {
        path: '',
        component: MainLayoutComponent,
        children: [
            { path: 'dashboard', component: DashboardComponent },
            /** Own-account settings: outside `/users` so dealer guards on user management never apply. */
            { path: 'profile-settings', component: ProfileSettingsComponent },
            {
                path: 'dealers',
                canMatch: [dealerRestrictionGuard],
                children: [
                    { path: '', component: DealersComponent },
                    { path: ':id', component: SingleDealerComponent },
                ],
            },
            {
                path: 'licenses',
                children: [
                    { path: '', component: LicensesComponent },
                    { path: ':id', component: SingleLicenseComponent },
                ],
            },
            {
                path: 'media-library',
                children: [
                    {
                        path: '',
                        component: MediaLibraryComponent,
                    },
                    {
                        path: ':id',
                        redirectTo: '/media-library',
                        pathMatch: 'full',
                    },
                ],
            },
            {
                path: 'users',
                children: [
                    {
                        path: 'profile-settings',
                        redirectTo: '/profile-settings',
                        pathMatch: 'full',
                    },
                    {
                        path: '',
                        component: UsersComponent,
                        canMatch: [dealerRestrictionGuard],
                    },
                    {
                        path: ':id',
                        component: SingleUserComponent,
                        canMatch: [superAdminOnlyGuard],
                    },
                ],
            },
            {
                path: 'playlists',
                children: [
                    {
                        path: '',
                        component: PlaylistsComponent,
                    },
                    {
                        path: 'create',
                        component: CreatePlaylistComponent,
                        data: { overflowHidden: true },
                    },
                    {
                        path: ':id',
                        component: SinglePlaylistComponent,
                    },
                ],
            },
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        ],
        canMatch: [authGuard],
    },
    {
        path: '',
        component: LoginComponent,
        canMatch: [guestGuard],
    },
    {
        path: 'auth',
        component: CallbackComponent,
    },
    {
        path: '',
        component: CheckSessionComponent,
        pathMatch: 'full',
    },
    {
        path: '**',
        component: CheckSessionComponent,
    },
];
